import { useEffect } from 'react';
import { isPartner } from '../helpers/appType';
import { useAuth } from '../store/auth';
import { User, UserRole } from '../types/user.types';
import { useUserConversationUnreadCount } from './useChatUnreadCount';

/**
 * Unread messages, in the browser tab.
 *
 * The one cue that costs nothing and that nothing outside the page can veto: a
 * tab is visible while the merchant works in another one, it survives being
 * away from the machine, and unlike a notification the operating system has no
 * say in whether it is shown.
 *
 * Merchant only. An admin has the inbox itself in front of them with a badge on
 * every row, and a count in the tab would be one more place saying the same
 * thing.
 *
 * The count comes from the same query the sidebar badge already runs, so this
 * costs no request of its own and the socket keeps it current.
 */

/** Anything past this is noise in a tab label. */
const MAX = 99;

const withCount = (title: string, unread: number) =>
  unread > 0 ? `(${unread > MAX ? `${MAX}+` : unread}) ${title}` : title;

/** Strips a count this hook wrote, so the base title never accumulates them. */
const stripCount = (title: string) => title.replace(/^\(\d+\+?\)\s+/, '');

export const useUnreadTabTitle = () => {
  const { role } = (useAuth((state) => state.user) ?? {}) as User;

  const { total } = useUserConversationUnreadCount({
    enabled:
      isPartner &&
      (role === UserRole.partner ||
        role === UserRole.manager ||
        role === UserRole.staff),
  });

  /* The support thread and the groups together, like the chat icon. */
  const unread = isPartner ? total : 0;

  useEffect(() => {
    const base = stripCount(document.title);
    document.title = withCount(base, unread);
    // Back to the plain title on the way out - the tab outlives this app's
    // mount when someone signs out.
    return () => {
      document.title = base;
    };
  }, [unread]);
};
